import type { ReactNode } from 'react';
import { createContext, useContext } from 'react';

// Which version the planner is reading, and whether that makes it read-only.
//
// Why a context and not just props: several components fetch
// `itineraries.byId` themselves rather than reading the page's data (the
// optional-sibling lookup in `item-row.tsx`, `subitem-form-modal.tsx`, the
// HyperGuest booking panel). If those kept passing no `versionId` they would
// read the LIVE version while the page renders a historical one — mixed data
// on screen. Reading the version off one context keeps every byId call in the
// planner tree pointed at the same version.
//
// The version itself lives in the URL (`?versionId=`), per the state-boundary
// rule — this context only distributes it, it does not own it. Lives in
// `entities/` so widgets and features can consume it without importing from
// the `pages/` layer.
type PlannerVersionContextValue = {
  /**
   * The historical version being viewed, or `null` when the planner is on the
   * live (active) version. Pass straight into `itineraries.byId` inputs.
   */
  versionId: string | null;
  /**
   * True while a historical version is being viewed. Every mutation affordance
   * in the planner must be disabled when this is set — the sold/live version
   * is what OPS and the client read, and it must not be touched from here.
   */
  isReadOnly: boolean;
};

const DEFAULT_VALUE: PlannerVersionContextValue = {
  versionId: null,
  isReadOnly: false,
};

const PlannerVersionContext =
  createContext<PlannerVersionContextValue>(DEFAULT_VALUE);

export const PlannerVersionProvider = ({
  children,
  value,
}: {
  children: ReactNode;
  value: PlannerVersionContextValue;
}) => (
  <PlannerVersionContext.Provider value={value}>
    {children}
  </PlannerVersionContext.Provider>
);

/**
 * Defaults to the live, editable version when no provider is mounted, so
 * components used outside the planner (item-edit page, tests) keep working
 * unchanged.
 */
export const usePlannerVersion = (): PlannerVersionContextValue =>
  useContext(PlannerVersionContext);

/** Convenience for the common "is every action disabled?" check. */
export const useIsPlannerReadOnly = (): boolean =>
  useContext(PlannerVersionContext).isReadOnly;
